/**
 * 3-dot context menu for World Template cards.
 * Options shown depend on ownership, collaboration role, and world privacy settings.
 */
import React, { useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/ui/confirm-delete";
import { ShareWorldDialog } from "@/components/world/ShareWorldDialog";
import { exportWorldTemplate } from "@/lib/export-utils";
import {
  MoreVertical,
  Eye,
  PenLine,
  Copy,
  Swords,
  Share2,
  Trash2,
  Loader2,
  Globe,
  FlaskConical,
  Download,
} from "lucide-react";

export interface WorldCardActionsProps {
  worldId: string;
  worldName: string;
  /** "owner" | "edit" | "play" | "view" | "public" */
  role: "owner" | "edit" | "play" | "view" | "public";
  allowCloning?: boolean;
  isPublic?: boolean;
  onView: () => void;
  onEdit?: () => void;
  onPlay: () => void;
  onPublishToggle?: () => void;
  onTestPlay?: () => void;
  onDuplicate?: () => Promise<void>;
  onDelete?: () => void;
}

export function WorldCardActions({
  worldId,
  worldName,
  role,
  allowCloning = false,
  isPublic = false,
  onView,
  onEdit,
  onPlay,
  onPublishToggle,
  onTestPlay,
  onDuplicate,
  onDelete,
}: WorldCardActionsProps) {
  const { t } = useLocale();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isOwnerOrEditor = role === "owner" || role === "edit";
  const canEdit = isOwnerOrEditor;
  const canExport = isOwnerOrEditor;
  const canDuplicate =
    (role === "owner" || role === "edit") ||
    (allowCloning && (role === "play" || role === "view" || role === "public"));
  const canDelete = role === "owner";
  const canShare = role === "owner";
  const canPublish = role === "owner";

  const handleDuplicate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDuplicate || isDuplicating) return;
    setIsDuplicating(true);
    try {
      await onDuplicate();
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportWorldTemplate(worldId, worldName, t);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="World options"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
          {/* View */}
          <DropdownMenuItem onClick={onView} className="gap-2 text-xs">
            <Eye className="w-3.5 h-3.5" />
            View Details
          </DropdownMenuItem>

          {/* Edit */}
          {canEdit && onEdit && (
            <DropdownMenuItem onClick={onEdit} className="gap-2 text-xs">
              <PenLine className="w-3.5 h-3.5" />
              Edit World
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Published: show "Play" (start game). Not published: show "Publish" + "Test Play" */}
          {canPublish && isPublic ? (
            <DropdownMenuItem onClick={onPlay} className="gap-2 text-xs">
              <Swords className="w-3.5 h-3.5" />
              Start Game
            </DropdownMenuItem>
          ) : canPublish && !isPublic ? (
            <>
              {onPublishToggle && (
                <DropdownMenuItem onClick={onPublishToggle} className="gap-2 text-xs">
                  <Globe className="w-3.5 h-3.5" />
                  Publish World
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onTestPlay || onPlay} className="gap-2 text-xs">
                <FlaskConical className="w-3.5 h-3.5" />
                Test Play
              </DropdownMenuItem>
            </>
          ) : (
            /* Non-owner roles always see Start Game */
            <DropdownMenuItem onClick={onPlay} className="gap-2 text-xs">
              <Swords className="w-3.5 h-3.5" />
              Start Game
            </DropdownMenuItem>
          )}

          {/* Duplicate */}
          {canDuplicate && onDuplicate && (
            <DropdownMenuItem
              onClick={handleDuplicate}
              disabled={isDuplicating}
              className="gap-2 text-xs"
            >
              {isDuplicating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              Duplicate
            </DropdownMenuItem>
          )}

          {/* Export */}
          {canExport && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleExport}
                disabled={isExporting}
                className="gap-2 text-xs"
              >
                {isExporting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Export Template
              </DropdownMenuItem>
            </>
          )}

          {/* Share / Invite */}
          {canShare && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShareOpen(true)}
                className="gap-2 text-xs"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share & Invite
              </DropdownMenuItem>
            </>
          )}

          {/* Delete (move to trash) */}
          {canDelete && onDelete && (
            <>
              <DropdownMenuSeparator />
              <ConfirmDelete
                onConfirm={onDelete}
                title="Move to trash?"
                description={`"${worldName}" will be moved to trash. You can restore it later.`}
                trigger={
                  <button className="flex items-center gap-2 w-full text-xs text-destructive hover:text-destructive px-2 py-1.5 rounded-sm hover:bg-accent cursor-default">
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete World
                  </button>
                }
              />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share dialog rendered outside dropdown to avoid portal issues */}
      {canShare && (
        <ShareWorldDialog
          worldId={worldId}
          worldName={worldName}
          trigger={<span />}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </>
  );
}
